import { ChangeEvent, FocusEvent, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { InputLogo } from "components/form/InputLogo"
import { InputPhone } from "components/form/InputPhone"

import { useAccountSettingsTabGuard } from "./navigationGuard"
import {
  AccountPageShell,
  enterpriseWebsiteValidationMessage,
  Field,
  Footer,
  getEnterpriseAdditionalInitialState,
  getEnterpriseWebsiteValidationError,
  isEnterpriseAdditionalDirty,
  isEnterpriseWebsiteValid,
  normalizeEnterpriseWebsiteUrl,
} from "./shared"
import { useUpdateEnterpriseAccount } from "./useUpdateEnterpriseAccount"

import styles from "./index.module.scss"

export const AdditionalInformationPage = () => {
  const { enterprise, isLoading, updateEnterprise } = useUpdateEnterpriseAccount()
  const initialValues = useMemo(() => getEnterpriseAdditionalInitialState(enterprise), [enterprise])
  const [additionalValues, setAdditionalValues] = useState(initialValues)
  const [enterpriseUrlError, setEnterpriseUrlError] = useState("")
  const isDirty = Boolean(enterprise && isEnterpriseAdditionalDirty(enterprise, additionalValues))
  const isEnterpriseUrlValid = isEnterpriseWebsiteValid(additionalValues.enterpriseUrl)
  const isSaveDisabled = !isDirty || !isEnterpriseUrlValid

  const handleAdditionalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setAdditionalValues(prev => ({ ...prev, [name]: value }))

    if (name === "enterpriseUrl" && !getEnterpriseWebsiteValidationError(value)) {
      setEnterpriseUrlError("")
    }
  }

  const handleEnterpriseUrlBlur = (event: FocusEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value

    const validationError = getEnterpriseWebsiteValidationError(value)

    if (!value.trim()) {
      setEnterpriseUrlError("")
      return
    }

    if (validationError) {
      setEnterpriseUrlError(validationError)
      return
    }

    setEnterpriseUrlError("")
    setAdditionalValues(prev => ({
      ...prev,
      enterpriseUrl: normalizeEnterpriseWebsiteUrl(value),
    }))
  }

  useEffect(() => {
    setAdditionalValues(initialValues)
  }, [initialValues])

  const handleSave = useCallback(async () => {
    if (!isEnterpriseUrlValid) {
      setEnterpriseUrlError(enterpriseWebsiteValidationMessage)
      return false
    }

    return updateEnterprise({ additionalValues })
  }, [additionalValues, isEnterpriseUrlValid, updateEnterprise])

  useAccountSettingsTabGuard({
    isDirty,
    isSaving: isLoading,
    onSave: handleSave,
  })

  return (
    <AccountPageShell footer={<Footer isLoading={isLoading} isSaveDisabled={isSaveDisabled} onSave={handleSave} />}>
      <h1 className={styles.title}>Additional Information</h1>
      <div className={styles.formGrid}>
        <Field label="Email" name="email" value={additionalValues.email} onChange={handleAdditionalChange} disabled />
        <Field
          label="Enterprise web URL (optional)"
          name="enterpriseUrl"
          value={additionalValues.enterpriseUrl}
          onChange={handleAdditionalChange}
          onBlur={handleEnterpriseUrlBlur}
          errorMessage={enterpriseUrlError}
        />
        <Field
          label="Full name (optional)"
          name="fullName"
          value={additionalValues.fullName}
          placeholder="Enter full name"
          onChange={handleAdditionalChange}
        />
        <InputPhone
          label="Phone number (optional)"
          name="phoneNumber"
          value={additionalValues.phoneNumber}
          onChange={value => setAdditionalValues(prev => ({ ...prev, phoneNumber: value }))}
        />
        <div className={classNames(styles.field, styles.fieldNarrow)}>
          <InputLogo
            id="enterpriseLogo"
            name="enterpriseLogo"
            label="Enterprise logo (optional)"
            value={additionalValues.image ? [additionalValues.image] : null}
            imageUrl={enterprise?.logo || enterprise?.image}
            className={styles.enterpriseLogo}
            formatNote="(Format: Png Ratio: 2:1 Max size: 750x375px)"
            onSetFile={files => setAdditionalValues(prev => ({ ...prev, image: files?.[0] }))}
            onValidationError={errorMessage => toast.error(errorMessage)}
          />
        </div>
      </div>
    </AccountPageShell>
  )
}
